// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Reload-before-prefill handoff (acceptance 4, 2026-09-04).
 *
 * When a CTA asks the Amy drawer to open with a prefilled prompt and the tab
 * is running a SUPERSEDED bundle (lib/deployFreshness.ts), the shell stashes
 * the prompt here, reloads, and — once the NEW bundle boots — reopens the
 * drawer with it. The stash carries the deployed build id that triggered the
 * reload: if the reload did not land on that build (offline, CDN lag) the
 * stash is dropped and the shell never reloads again for it (no loops).
 *
 * The composer's unsent text is stashed alongside (the reload is our own,
 * explicit one — there is no beforeunload handling anywhere).
 *
 * sessionStorage: per-tab, survives the reload, gone when the tab closes.
 * Every read/write is guarded — private mode or a blocked storage must
 * degrade to "no handoff", never to a broken CTA.
 */

export const PENDING_PREFILL_KEY = "bb.pendingAmyPrefill"
export const PENDING_DRAFT_KEY = "bb.pendingAmyDraft"

export interface PendingAmyPrefill {
  prompt: string
  /** pathname + search the CTA fired on (the reload keeps the URL; a mismatch means the handoff lost its context). */
  path: string
  /** The DEPLOYED build id that made the tab stale — the build the reload should land on. */
  buildId: string
}

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage
  } catch {
    return null
  }
}

export function stashPendingPrefill(pending: PendingAmyPrefill, store: Storage | null = storage()): void {
  try {
    store?.setItem(PENDING_PREFILL_KEY, JSON.stringify(pending))
  } catch {
    /* storage unavailable — the CTA simply opens without a handoff after reload */
  }
}

export function peekPendingPrefill(store: Storage | null = storage()): PendingAmyPrefill | null {
  try {
    const raw = store?.getItem(PENDING_PREFILL_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== "object" || parsed === null) return null
    const { prompt, path, buildId } = parsed as Record<string, unknown>
    if (typeof prompt !== "string" || typeof path !== "string" || typeof buildId !== "string") return null
    return { prompt, path, buildId }
  } catch {
    return null
  }
}

export function clearPendingPrefill(store: Storage | null = storage()): void {
  try {
    store?.removeItem(PENDING_PREFILL_KEY)
  } catch {
    /* nothing to clear */
  }
}

export type PendingPrefillOutcome =
  | { kind: "none" }
  /** The reload landed on the stashed build at the same route: open the drawer with this prompt. */
  | { kind: "open"; prompt: string }
  /** Stash existed but does not match this boot (other build or other route): dropped, never re-reloaded. */
  | { kind: "dropped"; reason: "build_mismatch" | "path_mismatch" }

/**
 * Boot-time consumer: ALWAYS clears the stash (one shot), and only yields
 * `open` when this boot IS the build the reload aimed for and the route is
 * the one the CTA fired on.
 */
export function takePendingPrefill(
  currentBuildId: string,
  currentPath: string,
  store: Storage | null = storage()
): PendingPrefillOutcome {
  const pending = peekPendingPrefill(store)
  if (!pending) return { kind: "none" }
  clearPendingPrefill(store)
  if (pending.buildId !== currentBuildId) return { kind: "dropped", reason: "build_mismatch" }
  if (pending.path !== currentPath) return { kind: "dropped", reason: "path_mismatch" }
  return { kind: "open", prompt: pending.prompt }
}

/** Composer draft: only non-blank text is worth carrying across the reload. */
export function stashPendingDraft(draft: string, store: Storage | null = storage()): void {
  try {
    if (draft.trim() === "") return
    store?.setItem(PENDING_DRAFT_KEY, draft)
  } catch {
    /* storage unavailable */
  }
}

/** One-shot read of the stashed draft (cleared on read). */
export function takePendingDraft(store: Storage | null = storage()): string | null {
  try {
    const draft = store?.getItem(PENDING_DRAFT_KEY) ?? null
    store?.removeItem(PENDING_DRAFT_KEY)
    return draft && draft.trim() !== "" ? draft : null
  } catch {
    return null
  }
}
